import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { CommonDossierComponent } from '@kv-common-components/kv-common-components';
import {
    ArmService,
    DocumentGed,
    EspaceDocumentaireService,
} from '@kv-common-components/kx-services';
import { Appointment } from '@modules/souscription/document-medicale/document-list/appointment';
import { FormatHelperService } from '@shared/services/format-helper.service';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class DocumentListHelperService extends CommonDossierComponent {
    static readonly typologieDocDemandePiecesMedicales = 'PIJ3001';
    static readonly typologieDocRelancePiecesMedicales = 'PIJ3003';

    static readonly attributeDocCorrectLibelle = 'correctLibelle';
    static readonly attributeDocTypologie = 'typologie';
    static readonly attributeDocTypeEmprunteur = 'typeEmprunteur';
    static readonly attributeExamIdApiTypeExamen = 'idApiTypeExamen';
    static readonly attributeExamIdApi = 'idApi';

    static readonly geolocApiUrl = `https://maps.googleapis.com/maps/api/js?key=${environment.geolocApiKey}&libraries=places`;

    sentDocuments: any[] = [];
    neededDocuments: any[] = [];
    documentsToConsent: any[] = [];
    allDocsWithStatus: any[] = [];
    filterDoc: any;

    constructor(
        logger: NGXLogger,
        espaceDoc: EspaceDocumentaireService,
        private armService: ArmService
    ) {
        super(logger, espaceDoc);
    }

    //Returns a global unique identifier
    static guid(): string {
        const s4 = () =>
            Math.floor((1 + DocumentListHelperService.cryptoRand()) * 0x10000)
                .toString(16)
                .substring(1);
        return (
            s4() +
            s4() +
            '-' +
            s4() +
            '-' +
            s4() +
            '-' +
            s4() +
            '-' +
            s4() +
            s4() +
            s4()
        );
    }

    static cryptoRand(): number {
        const randomBuffer = new Uint32Array(1);
        (window.crypto || (window as any).msCrypto).getRandomValues(
            randomBuffer
        );
        return randomBuffer[0] / (0xffffffff + 1);
    }

    static getMostRecentDoc(documentList): any {
        let mostRecentDoc = documentList[0];
        documentList.forEach((doc) => {
            if (
                new Date(doc.dateDepot).getTime() >
                new Date(mostRecentDoc.dateDepot).getTime()
            ) {
                mostRecentDoc = doc;
            }
        });
        return mostRecentDoc;
    }

    static createAppointmentFromArm(
        rdvArm: any,
        praticienArm: any
    ): Appointment {
        const appointment = new Appointment();
        appointment.idRdvMedical = rdvArm.idApi.toString();
        appointment.dateDebut = rdvArm.dateDebut;
        appointment.dateFin = rdvArm.dateFin;
        appointment.labelDate =
            FormatHelperService.getDateFormattedForAppointment(
                rdvArm.dateDebut,
                FormatHelperService.dateFormatAppointment
            );
        appointment.labelPraticien = praticienArm.libelleCentre
            ? praticienArm.libelleCentre
            : praticienArm.libelle;
        appointment.praticien = praticienArm;

        return appointment;
    }

    static getLastDocumentForExpectedMedicalDocs(
        espaceDocumentaire: DocumentGed[]
    ): any {
        let documentList = espaceDocumentaire.filter(
            (doc) =>
                doc.typologie ===
                DocumentListHelperService.typologieDocRelancePiecesMedicales
        );

        if (documentList.length === 0) {
            documentList = espaceDocumentaire.filter(
                (doc) =>
                    doc.typologie ===
                    DocumentListHelperService.typologieDocDemandePiecesMedicales
            );
        }

        return DocumentListHelperService.getMostRecentDoc(documentList);
    }

    // get filtered pieces from Espace client
    getDocsFromEspaceClt(idDossier: number, espaceClient: any): any[] {
        const projetCredit = espaceClient.listeProjetsCredits.find(
            (projet) => projet.idProjetCredit === idDossier
        );
        const emprunteur = projetCredit.listeEmprunteurs.find(
            (emp) => emp.idPersonne === espaceClient.personne.idPersonne
        );
        // duplicate Libelle Key to differenciate from Libelle key in other list
        const listePieces = this.duplicateLibelleKey(
            emprunteur.assurance.listePieces
        );

        return listePieces.filter(
            (piece) =>
                (this.isExpectedMedicalDocument(piece) ||
                    this.isExpectedAdministrativeDocument(piece)) &&
                !this.isMedicalQuestionnaire(piece) &&
                !this.isLiasseDocument(piece) &&
                !this.isfilteredCodeAIA(piece) &&
                !this.isBAQS(piece)
        );
    }

    // get medical docs that are in espace doc (already uploaded)
    getDocsFromEspaceDoc(
        idPersonne: string,
        idPropositionPrincipale: string
    ): Observable<DocumentGed[]> {
        return this.espaceDocumentaireService
            .getEspaceDocumentaire(idPersonne, idPropositionPrincipale)
            .pipe(
                map((documentList) =>
                    documentList.filter(
                        (doc) =>
                            doc.sens === 'entrant' &&
                            (doc.theme === 'PARCOURS_MEDICAL' ||
                                doc.theme === 'ADHESIONS')
                    )
                )
            );
    }

    // returns true if the document was uploaded to the docsFromEspaceDoc, returns false otherwise
    docWasUploadedToEspaceDoc(
        document: any,
        docsFromEspaceDoc: DocumentGed[]
    ): boolean {
        if (this.filesProvided(document) || this.filesConforme(document)) {
            return true;
        }
        let docWasUploaded = false;
        docsFromEspaceDoc.forEach((doc) => {
            if (doc.typologie === document.typologie) {
                docWasUploaded = true;
            }
        });
        return docWasUploaded;
    }

    // returns a merged list of list 1 and list2 based on mergeAttributes
    merge(
        list1: { list: any[]; mergeAttribute: string },
        list2: { list: any[]; mergeAttribute: string }
    ): any[] {
        const mergedList = [];
        list1.list.forEach((element1) => {
            const element2 = list2.list.find(
                (doc) =>
                    doc[list2.mergeAttribute] === element1[list1.mergeAttribute]
            );
            // garanties that only elements which are in the intersection of the 2 lists are remaining
            if (element2) {
                mergedList.push({
                    ...element1,
                    ...element2,
                });
            }
        });
        return mergedList;
    }

    duplicateLibelleKey(list: any[]): any[] {
        list.forEach((element) => (element.correctLibelle = element.libelle));
        return list;
    }

    sortListByAttribute(list: any[], attribute: string): any[] {
        return list.sort((a, b) => (a[attribute] > b[attribute] ? 1 : -1));
    }

    getMedicalDocumentsByStatus(
        currentEspaceClient: any,
        userValue: any,
        waitingDoc: string
    ): Observable<any> {
        return new Observable<any>((observer) => {
            const docsFromEspaceClt = this.getDocsFromEspaceClt(
                Number(userValue.idDossier),
                currentEspaceClient
            );
            this.getDocsFromEspaceDoc(
                currentEspaceClient.personne.idPersonne,
                userValue.idPropositionPrincipale
            ).subscribe((docsFromEspaceDoc) => {
                this.enrichDocumentsFromEspaceDoc(
                    docsFromEspaceDoc,
                    docsFromEspaceClt
                );
                this.armService.getTypesExamens().subscribe((typesExamens) => {
                    docsFromEspaceClt.forEach((doc) => {
                        const examen = typesExamens.find(
                            (typeExamen) =>
                                typeExamen.typeEmprunteur === doc.typologie
                        );
                        if (examen) {
                            doc.idApi = examen.idApi;
                            doc.typeEmprunteur = examen.typeEmprunteur;
                            doc.typePrevoyance = examen.typePrevoyance;
                        }
                    });
                    const docList = docsFromEspaceClt.filter(
                        (doc) =>
                            !this.docWasUploadedToEspaceDoc(
                                doc,
                                docsFromEspaceDoc
                            ) && doc.idApi
                    );
                    this.neededDocuments = this.sortListByAttribute(
                        docList,
                        'correctLibelle'
                    );
                    const docListSent = docsFromEspaceClt.filter((doc) =>
                        this.docWasUploadedToEspaceDoc(doc, docsFromEspaceDoc)
                    );
                    this.sentDocuments = this.sortListByAttribute(
                        docListSent,
                        'correctLibelle'
                    );
                    // combines both sent and not sent docs with their respective transmis status
                    this.allDocsWithStatus = [];
                    const allDocs = this.sortListByAttribute(
                        docsFromEspaceClt,
                        'correctLibelle'
                    );
                    allDocs.forEach((element) => {
                        this.allDocsWithStatus.push({
                            ...element,
                            transmis:
                                this.docWasUploadedToEspaceDoc(
                                    element,
                                    docsFromEspaceDoc
                                ) ||
                                this.filesProvided(element) ||
                                this.filesConforme(element),
                        });
                    });

                    if (waitingDoc === 'needed') {
                        observer.next(this.neededDocuments);
                        observer.complete();
                    } else if (waitingDoc === 'allDocsWithStatus') {
                        observer.next(this.allDocsWithStatus);
                        observer.complete();
                    } else {
                        this.documentsToConsent = [];

                        this.resolveDocumentState(this.neededDocuments, false);
                        this.resolveDocumentState(this.sentDocuments, true);
                        observer.next(this.documentsToConsent);
                        observer.complete();
                    }
                });
            });
        });
    }

    resolveDocumentState(documents: any[], status: boolean) {
        documents.forEach((doc) => {
            const documentState = {
                idApiTypeExamen: doc.typologie,
                transmis: status,
            };
            this.documentsToConsent.push(documentState);
        });
        return this.documentsToConsent;
    }

    getExpectedDocumentList(
        idDossier: number,
        espaceClt: any,
        typesExamens: any[],
        dossierMedical: any
    ): any[] {
        const docsFromEspClt = this.getDocsFromEspaceClt(idDossier, espaceClt);

        const examList = this.merge(
            {
                list: typesExamens,
                mergeAttribute: DocumentListHelperService.attributeExamIdApi,
            },
            {
                list: dossierMedical.listeExamen,
                mergeAttribute:
                    DocumentListHelperService.attributeExamIdApiTypeExamen,
            }
        );

        const mergedList = this.merge(
            {
                list: docsFromEspClt,
                mergeAttribute: DocumentListHelperService.attributeDocTypologie,
            },
            {
                list: examList,
                mergeAttribute:
                    DocumentListHelperService.attributeDocTypeEmprunteur,
            }
        );

        const documentList = this.sortListByAttribute(
            mergedList,
            DocumentListHelperService.attributeDocCorrectLibelle
        );
        for (const doc of documentList) {
            doc.label = doc.typologie.toLowerCase();
            doc.label =
                '- ' + doc.label.charAt(0).toUpperCase() + doc.label.slice(1);
        }

        return documentList;
    }

    private isExpectedMedicalDocument(piece: any): boolean {
        return ['Médecin', 'Médical'].includes(piece.typePiece);
    }

    private isExpectedAdministrativeDocument(piece: any): boolean {
        return piece && piece.typePiece === 'Administrative';
    }

    private isMedicalQuestionnaire(piece: any): boolean {
        return piece.typologie === 'QUESTIONNAIRE DE SANTE MANUEL';
    }

    private isLiasseDocument(piece: any): boolean {
        return piece.typologie.toUpperCase().includes('LIASSE');
    }

    private isfilteredCodeAIA(piece: any): boolean {
        return this.filtredCodeAIADocuments().includes(piece.codeAIA);
    }

    private isBAQS(piece: any): boolean {
        const regexBAQSTypologies =
            /^(BAQS )(.+)( ELECTRONIQUE| MANUEL| AUTOMATIQUE)$/;
        return regexBAQSTypologies.test(piece.typologie);
    }

    private filtredCodeAIADocuments(): string[] {
        if (environment.filteredCodeAIADocuments) {
            return environment.filteredCodeAIADocuments.split(',');
        }
        return [];
    }

    private enrichDocumentsFromEspaceDoc(
        docsFromEspaceDoc: any[],
        docsFromEspaceClt: any[]
    ): void {
        this.filterDocByTypologie(
            'MEDICAL_DOCUMENT',
            docsFromEspaceDoc,
            environment.contractualDocument,
            environment.infoDocument
        );
        docsFromEspaceClt.forEach((doc) => {
            const foundDoc = this.filterDoc.find(
                (d) => d.typologie === doc.typologie
            );
            //Ajouter l'attribut idGedEspDoc pour pouvoir également avoir idGed de l'espace documentaire
            doc.idGedEspDoc = foundDoc ? foundDoc.idGed : undefined;
        });
    }
}
